import { type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';

interface FeaturePoint {
  title: string;
  description: string;
}

interface FeaturePageLayoutProps {
  // Hero Section
  badge?: string;
  title: string;
  subtitle: string;
  heroImage?: string; // URL or placeholder
  heroImageAlt?: string;

  // Features/Benefits
  features?: FeaturePoint[];

  // Screenshot Sections
  sections?: {
    title: string;
    description: string;
    image?: string;
    imageAlt?: string;
    imageSide?: 'left' | 'right';
    bullets?: string[];
  }[];

  // CTA
  ctaTitle?: string;
  ctaDescription?: string;

  // Additional content
  children?: ReactNode;
}

export default function FeaturePageLayout({
  badge,
  title,
  subtitle,
  heroImage,
  heroImageAlt = 'Feature Screenshot',
  features,
  sections,
  ctaTitle = 'Bereit loszulegen?',
  ctaDescription = 'Testen Sie Saldify 14 Tage kostenlos und überzeugen Sie sich selbst.',
  children,
}: FeaturePageLayoutProps): ReactElement {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary-50 via-white to-secondary-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary-100/50 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {badge && (
                <span className="inline-block bg-primary-100 text-primary-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                  {badge}
                </span>
              )}
              <h1 className="text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
                {title}
              </h1>
              <p className="text-lg text-secondary-600 mb-8">
                {subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/#preise"
                  className="btn-primary px-6 py-3 shadow-lg shadow-primary-600/30"
                >
                  Kostenlos testen
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#demo"
                  className="btn-outline px-6 py-3"
                >
                  Demo ansehen
                </a>
              </div>
            </div>

            {/* Hero Image / Screenshot Placeholder */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-4 ">
                {heroImage ? (
                  <img
                    src={heroImage}
                    alt={heroImageAlt}
                    loading="eager"
                    className="rounded-xl w-full border border-secondary-100"
                  />
                ) : (
                  <ImagePlaceholder
                    label="Screenshot: Hero Image"
                    aspectRatio="16/10"
                  />
                )}
              </div>
              {/* Gruenes Abzeichen "Einfach & Intuitiv" entfernt: eine
                  Behauptung ohne Inhalt, und die einzige gruene Flaeche auf
                  sonst blauen Seiten. */}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      {features && features.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                Ihre Vorteile im Überblick
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-secondary-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <Check className="w-5 h-5 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-secondary-600 text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Screenshot Sections */}
      {sections && sections.map((section, index) => (
        <section
          key={index}
          className={`py-16 lg:py-24 ${index % 2 === 0 ? 'bg-secondary-50' : 'bg-white'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${
              section.imageSide === 'left' ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content */}
              <div className={section.imageSide === 'left' ? 'lg:order-2' : ''}>
                <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                  {section.title}
                </h2>
                <p className="text-lg text-secondary-600 mb-6">
                  {section.description}
                </p>
                {section.bullets && (
                  <ul className="space-y-3">
                    {section.bullets.map((bullet, bulletIndex) => (
                      <li key={bulletIndex} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" />
                        <span className="text-secondary-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Image */}
              <div className={section.imageSide === 'left' ? 'lg:order-1' : ''}>
                <div className="bg-white rounded-2xl shadow-xl p-4">
                  {section.image ? (
                    <img
                      src={section.image}
                      alt={section.imageAlt || section.title}
                      loading="lazy"
                      className="rounded-xl w-full border border-secondary-100"
                    />
                  ) : heroImage ? (
                    <img
                      src={heroImage}
                      alt={heroImageAlt}
                      loading="lazy"
                      className="rounded-xl w-full border border-secondary-100"
                    />
                  ) : (
                    <ImagePlaceholder label={`Screenshot: ${section.title}`} aspectRatio="16/10" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Additional Content */}
      {children}

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-primary-600 to-primary-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {ctaTitle}
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            {ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#preise"
              className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg inline-flex items-center justify-center gap-2"
            >
              Jetzt kostenlos starten
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#kontakt"
              className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all inline-flex items-center justify-center"
            >
              Beratung anfordern
            </a>
          </div>
          <p className="text-primary-200 text-sm mt-6">
            14 Tage kostenlos • Keine Kreditkarte • Jederzeit kündbar
          </p>
        </div>
      </section>
    </div>
  );
}

// Image Placeholder Component
interface ImagePlaceholderProps {
  label: string;
  aspectRatio?: string;
}

export function ImagePlaceholder({ label, aspectRatio = '16/9' }: ImagePlaceholderProps): ReactElement {
  return (
    <div
      className="bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-xl flex flex-col items-center justify-center text-secondary-400 border-2 border-dashed border-secondary-300"
      style={{ aspectRatio }}
    >
      <svg
        className="w-16 h-16 mb-3 opacity-50"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}
